import { TFunction } from "i18next";
import { array, object, ObjectSchema, string } from "yup";
import { Role, RoleName, roleSchema, StandardRole } from "../users/Role";
import { fieldDefinedError, fieldMin1Error } from "../../shared/technical/rest/validation";
import { getTranslatedValue, TranslatedValue, translatedValueSchema } from "../../shared/technical/i18n/TranslatedValue";
import { GUI_LOCALES } from "../../shared/technical/i18n/i18n";
import { User } from "../users/User";
import { isAdmin, isAdminForApplication, isApplicationAdmin, isCompanyAdmin } from "../users/AccountServiceRoles";

export type ApplicationName = string;

export interface LightWeightApplication {
  id?: string,
  name: ApplicationName
}

export interface Application {
  id?: string,
  name: ApplicationName,
  keycloakClientId?: string,
  adminRole?: RoleName,
  url?: string,
  roles: Role[],
  displayNames: TranslatedValue[],
  validRedirectUris?: string[]
}

export const lightWeightApplicationSchema: ObjectSchema<LightWeightApplication> = object({
  id: string(),
  name: string().defined()
})

export function applicationSchema(t: TFunction): ObjectSchema<Application> {
  return object({
    id: string(),
    name: string().trim().required(fieldDefinedError(t)),
    keycloakClientId: string().trim().required(fieldDefinedError(t)),
    adminRole: string().default(StandardRole.ADMIN).defined(fieldDefinedError(t)),
    url: string().trim().required(fieldDefinedError(t)),
    roles: array(roleSchema(t)).default([]),
    displayNames: array(translatedValueSchema(t)).default([]).min(0, fieldMin1Error(t)),
    validRedirectUris: array(string().trim().defined())
  })
}

export function defaultApplication(t: TFunction) {
  return applicationSchema(t).getDefault() as Application
}

export const ACCOUNT_SERVICE = { id: '1', name: 'ACCOUNT_SERVICE' };

export function isAccountService(application?: Application | LightWeightApplication) {
  return !!application && application.name === ACCOUNT_SERVICE.name
}

export function getApplicationByName(applications: Application[], name: ApplicationName) {
  return applications.find(application => application.name === name)
}

export function getApplicationById(applications: Application[], id: string) {
  return applications.find(application => application.id === id)
}

export function formatApplication(application: Application) {
  return getTranslatedValue(application.displayNames) || application.name
}

export function translationsComplete(application: Application): boolean {
  if (!application.displayNames || application.displayNames.length < GUI_LOCALES.length) {
    return false
  }
  const transMissing = application.roles.find((role) => (!role.displayNames || role.displayNames.length < GUI_LOCALES.length))
  return !transMissing
}

export function translationsOfNameComplete(application: Application): boolean {
  return !(!application.displayNames || application.displayNames.length < GUI_LOCALES.length);

}

export function translationsOfRolesComplete(application: Application): boolean {
  const transMissing = application.roles.find((role) => (!role.displayNames || role.displayNames.length < GUI_LOCALES.length))
  return !transMissing
}

export function isCompanyAdminAccessibleApplication(account: User, application: Application) {
  return isCompanyAdmin(account) && account.applicationRoles.some(applicationRole => applicationRole.application.name === application.name)
}

/**
 * Returns the applications that the user (account) may assign.
 */
export function filterAssignableApplications(applications: Application[], account: User) {
  if (isAdmin(account)) {
    return applications;
  } else if (isCompanyAdmin(account)) {
    // Company admins who are application admins as well (SSO-292) only edit users of their company, the server applies
    // the company admin rules there
    return applications.filter(application => isCompanyAdminAccessibleApplication(account, application));
  } else if (isApplicationAdmin(account)) {
    return applications.filter(application => isAdminForApplication(account, application) || isAccountService(application))
  }
  return [];
}

/**
 * Returns the applications that the user (account) may see.
 * A company admin may be application admin as well (SSO-292), then he sees the applications of both roles.
 */
export function filterVisibleApplications(applications: Application[], account: User) {
  if (isAdmin(account)) {
    return applications;
  } else if (isApplicationAdmin(account) || isCompanyAdmin(account)) {
    return applications.filter(application =>
      (isApplicationAdmin(account) && isAdminForApplication(account, application)) ||
      isCompanyAdminAccessibleApplication(account, application))
  }
  return applications.filter(application => account.applicationRoles.some(applicationRole => applicationRole.application.name === application.name));
}
